import { EmployeeAvailableListResponse, EmployeeListDto, PayPeriodViewModel, EmployeeRoleEnum, EdmDate, EmployeeCalendarDailyDto } from "interfaces/Mueller.Business.Models";
import React, { useEffect, useRef, useState } from "react";
import { InitialState } from "reducers/initialState";
import { getPayPeriodList } from "actions/casetimelog";
import { getEmployeesByRoles } from "requests/employee";
import { userHasRight } from "selectors/rights";
import { MANAGEMENT_CALENDAR_EDIT } from "typeConstants/rights";
import { connect } from "react-redux";
import PayPeriodDropdown from "components/User/PayPeriodDropdown";
import { MenuButton } from "react-md";
import { MCard, MGrid } from "components/Shared";
import "./EmployeeCalendarList.scss";
import { getEmployeeAvailableList } from "requests/employeeCalendarEvent";
import { MGridColumnDefinition } from "components/Shared/MGrid";
import { DATA_TYPE_NUMBER } from "typeConstants/dataTypes";
import LoadingCover from "components/Shared/LoadingCover";

interface Props {
  match: { params: { calType: "qa" | "customer-experience" } },
  getPayPeriodList: () => void,
  payPeriods: PayPeriodViewModel[],
};

const calTypeRoleMap = {
  "qa": {
    managerRole: EmployeeRoleEnum.BUSOP_MANAGER,
    employeeRole: EmployeeRoleEnum.BUSOP_AGENT,
  },
  "customer-experience": {
    managerRole: EmployeeRoleEnum.CUSTOMER_EXPERIENCE_MANAGER,
    employeeRole: EmployeeRoleEnum.CUSTOMER_EXPERIENCE_REP,
  },
};

const EmployeeCalendarList = ({ match: { params: { calType } }, getPayPeriodList, payPeriods }: Props) => {
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeListDto>();
  const [selectedPayPeriodIndex, setSelectedPayPeriodIndex] = useState<number>(0);
  const [employees, setEmployees] = useState<EmployeeListDto[]>();
  const [calendarData, setCalendarData] = useState<EmployeeAvailableListResponse>();
  const [isLoadingCalendarData, setIsLoadingCalendarData] = useState<boolean>(false);

  const mounted = useRef(true);
  
  const employeeRole = calTypeRoleMap[calType];

  useEffect(() => {
    getPayPeriodList();
    getEmployeesByRoles([employeeRole.managerRole], setEmployees, mounted);

    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!payPeriods?.length) return;

    const ppDate = (pp: EdmDate) => new Date(pp.year, pp.month - 1, pp.day);

    if (selectedEmployee) {
      setIsLoadingCalendarData(true);
      getEmployeeAvailableList(
        selectedEmployee.employeeId,
        {
          employeeRoleRequested: employeeRole.employeeRole,
          startDateTime: ppDate(payPeriods[selectedPayPeriodIndex].fromDate),
          endDateTime: ppDate(payPeriods[selectedPayPeriodIndex].toDate),
        },
        calData => {
          setCalendarData(calData);
          setIsLoadingCalendarData(false);
        },
        null,
        mounted
      );
    } else setCalendarData(null);
  }, [selectedEmployee, payPeriods, selectedPayPeriodIndex]);

  return (
    <MCard title="Employee List" className="employee-calendar-list">
      <div className="filters">
        <PayPeriodDropdown
          payPeriods={payPeriods}
          selectedIndex={selectedPayPeriodIndex}
          payPeriodChangeCallback={setSelectedPayPeriodIndex}
        />
        <MenuButtonDropdown
          items={employees?.map(e => ({ label: e.firstLastName, value: e })) ?? []}
          label={selectedEmployee?.firstLastName ?? "Select an Employee"}
          onChange={e => setSelectedEmployee(e.value)}
        />
      </div>
      <hr style={{ width: "100%" }} />
      <div className="employee-calendar-container">
        {calendarData && 
        <MGrid
          key={`${isLoadingCalendarData}`} // have to refresh calendar so the header renderer gets the new totals
          data={calendarData.employees}
          columnDefinitions={getEmployeeCalendarColumns(calendarData.totals)}
        />}
        {isLoadingCalendarData && <LoadingCover loadingMessages={["Loading Calendar Data..."]} />}
      </div>
    </MCard>
  );
};

const mapStateToProps = (state: InitialState) => ({
  canEdit: userHasRight(MANAGEMENT_CALENDAR_EDIT)(state),
  payPeriods: state.caseTimeLog.payPeriods,
});

const mapDispatchToProps = (dispatch) => ({
  getPayPeriodList() {
    dispatch(getPayPeriodList());
  },
});

interface MenuButtonItem {
  label: string;
  value?: any;
}

interface MenuButtonProps
{
  items?: MenuButtonItem[],
  label: string,
  onChange: (item: MenuButtonItem) => void,
};

const MenuButtonDropdown = ({ items, label, onChange }: MenuButtonProps) => {
  return (
    <MenuButton
      className="md-button-dropdown-toggle"
      // @ts-ignore
      position={MenuButton.Positions.BELOW}
      primary
      raised
      menuItems={items?.map(i => ({ primaryText: i.label, onClick: () => onChange(i) })) ?? []}
      iconChildren="arrow_drop_down"
    >
      {label}
    </MenuButton>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeCalendarList);



const getFilterDiv = (value: number) => () => <div style={{ textAlign: "right" }}>{value.toFixed(2)}</div>;

const getEmployeeCalendarColumns = (totals: EmployeeCalendarDailyDto): MGridColumnDefinition<EmployeeCalendarDailyDto>[] => [
  {
    key: "employee",
    name: "Name",
    dataSource: row => row.employee.firstLast,
  },
  {
    key: "availableHoursTotal",
    name: "Available Hours",
    dataSource: "availableHoursTotal",
    sortable: true,
    dataType: DATA_TYPE_NUMBER,
    filterRenderer: getFilterDiv(totals.availableHoursTotal),
  },
  {
    key: "sunday",
    name: "Sunday",
    dataSource: "sundayAvailableHours",
    sortable: true,
    dataType: DATA_TYPE_NUMBER,
    filterRenderer: getFilterDiv(totals.sundayAvailableHours),
  },
  {
    key: "monday",
    name: "Monday",
    dataSource: "mondayAvailableHours",
    sortable: true,
    dataType: DATA_TYPE_NUMBER,
    filterRenderer: getFilterDiv(totals.mondayAvailableHours),
  },
  {
    key: "tuesday",
    name: "Tuesday",
    dataSource: "tuesdayAvailableHours",
    sortable: true,
    dataType: DATA_TYPE_NUMBER,
    filterRenderer: getFilterDiv(totals.tuesdayAvailableHours),
  },
  {
    key: "wednesday",
    name: "Wednesday",
    dataSource: "wednesdayAvailableHours",
    sortable: true,
    dataType: DATA_TYPE_NUMBER,
    filterRenderer: getFilterDiv(totals.wednesdayAvailableHours),
  },
  {
    key: "thursday",
    name: "Thursday",
    dataSource: "thursdayAvailableHours",
    sortable: true,
    dataType: DATA_TYPE_NUMBER,
    filterRenderer: getFilterDiv(totals.thursdayAvailableHours),
  },
  {
    key: "friday",
    name: "Friday",
    dataSource: "fridayAvailableHours",
    sortable: true,
    dataType: DATA_TYPE_NUMBER,
    filterRenderer: getFilterDiv(totals.fridayAvailableHours),
  },
  {
    key: "saturday",
    name: "Saturday",
    dataSource: "saturdayAvailableHours",
    sortable: true,
    dataType: DATA_TYPE_NUMBER,
    filterRenderer: getFilterDiv(totals.saturdayAvailableHours),
  },
];
